import { useTranslation } from 'react-i18next';
import { Accordion, Group, Stack, Text } from '@mantine/core';
import DocRow from '@/components/app/deals/steps/forwarding/DocRow';
import StageSection from '@/components/app/deals/steps/forwarding/StageSection';
import VehicleRow from '@/components/app/deals/steps/forwarding/VehicleRow';
import { countVehiclesLoaded, isDailyUpd, remaining } from '@/lib/forwardingView';
import { fmtFwdDate } from '@/lib/forwarding';
import { formatMoney } from '@/lib/format';
import type { ForwardingInfo } from '@/types/deals';

interface LoadedWeightProps {
  readonly info: ForwardingInfo;
}

// Этап «Отгруженный вес»: загружено экспедитором на ТС (кг), по дням. День → карточки ТС (с весом,
// водителем, документами ТС) + документы дня (Расписка экспедитора, УПД за дату с подписями).
export default function LoadedWeight({ info }: LoadedWeightProps) {
  const { t } = useTranslation();
  const loaded = info.loadedDeliveryInfo;
  const days = (loaded?.loadingDays ?? []).filter((d) => d.started);

  if (!days.length && !loaded?.loadedWeightKg) {
    return null;
  }

  return (
    <StageSection
      title={t('app.deals.forwarding.loaded')}
      unit={t('app.deals.forwarding.kg')}
      doneLabel={t('app.deals.forwarding.loadedDone')}
      done={loaded?.loadedWeightKg}
      total={loaded?.totalWeightKg}
      remainingLabel={t('app.deals.forwarding.loadedRemaining')}
      remaining={remaining(loaded?.totalWeightKg, loaded?.loadedWeightKg)}
      count={countVehiclesLoaded(info)}
      countLabel={t('app.deals.forwarding.vehiclesShort')}
      emptyText={days.length ? undefined : t('app.deals.forwarding.allocating')}
      compact
    >
      <Stack gap="sm">
        {loaded?.forwarderReport ? <DocRow doc={loaded.forwarderReport} /> : null}
        <Accordion variant="separated" radius="md" multiple defaultValue={[String(days.length - 1)]}>
          {days.map((day, i) => (
            <Accordion.Item key={day.loadingDate ?? i} value={String(i)}>
              <Accordion.Control>
                <Group gap="md" wrap="nowrap">
                  <Text fz="sm" fw={500}>
                    {fmtFwdDate(day.loadingDate) ?? '—'}
                  </Text>
                  <Text fz="sm" c="gray.7">
                    {day.vehicles?.length ?? 0} {t('app.deals.forwarding.vehiclesShort')}
                  </Text>
                  <Text fz="sm" c="gray.8">
                    {formatMoney(day.loadedWeightKg)} {t('app.deals.forwarding.kg')}
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  {(day.vehicles ?? []).map((v, vi) => (
                    <VehicleRow key={v.ttnId ?? `${i}-${vi}`} vehicle={v} index={vi + 1} />
                  ))}
                  {/* Дневной УПД исключаем — он показан в отдельной секции «Подпись УПД» (без дубля). */}
                  {(day.documents ?? [])
                    .filter((doc) => !isDailyUpd(doc))
                    .map((doc, di) => (
                      <DocRow key={doc.id ?? `${i}-${di}`} doc={doc} />
                    ))}
                  {(day.documents ?? []).length === 0 ? (
                    <Text fz="xs" c="gray.5">
                      {t('app.deals.forwarding.docsAwaitPeriod')}
                    </Text>
                  ) : null}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>
    </StageSection>
  );
}
